import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import ScreenshotSupport from './ScreenshotSupport';
import MaximizeModal from './MaximizeModal';
import useDraggableModal from 'hooks/useDraggableModal';
import { MainContext } from 'contexts/MainContext';

interface SalesPriceProps {
  currency: string;
  price: number;
  saleValue: string;
  setSaleValue: (value: string) => void;
  onClickApply: () => void;
}

const SalesPrice: FC<SalesPriceProps> = ({ currency, ...props }) => {
  const { t } = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [displaySaleValue, setDisplaySaleValue] = useState('');
  const { user, initSiteData } = useContext(MainContext);
  const [urlParams, setUrlParams] = useSearchParams();
  const [visibleCouponAmount, setVisibleCouponAmount] = useState(true);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);

  useEffect(() => {
    setVisibleCouponAmount(initSiteData?.other?.visibleCouponAmount === 'true' || false);

    const couponAmount = urlParams.get('couponAmount');
    if (couponAmount) {
      props.setSaleValue(couponAmount);
      setDisplaySaleValue(couponAmount);
    }
  }, [initSiteData]); // eslint-disable-line

  const getSalesPrice = () => {
    if (!parseInt(props.saleValue || '0')) return props.price;
    return props.price + parseInt(props.saleValue || '0');
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      await props.onClickApply();
      setShowSalesModal(false);
      setLoading(false);
    })();
  }, [props.saleValue]); // eslint-disable-line

  const submit = async () => {
    props.setSaleValue(displaySaleValue);
    const newParams = new URLSearchParams(urlParams);
    newParams.set('couponAmount', displaySaleValue);
    setUrlParams(newParams);
    if (props.saleValue === displaySaleValue) {
      setShowSalesModal(false);
    }
  };

  return (
    <Fragment>
      {user?.allowCoupons && visibleCouponAmount && (
        <div className="details-summary book-summary default-shadow mt-20">
          <div className="title-underline text-lg mb-10 title-header-grey">
            {t('hotel.sales_price')}
          </div>
          <div className="display-flex flex-wrap">
            {!!props.saleValue && <Fragment>
              <div className="mr-10" style={{ width: 40 }}>
                <SelectCustom
                  options={[{ value: currency, label: getCurrency(currency) }]}
                  value={currency}
                  disabled
                  onChange={() => { }}
                />
              </div>
              <div style={{ marginTop: -5, flex: '1 0 calc(100% - 200px)' }}>
                <FormGroup
                  value={props.saleValue}
                  onChange={props.setSaleValue}
                  label=""
                  allowOnlyNumbers
                  disabled={true}
                />
              </div>
            </Fragment>
            }

            <Button
              className={"btn btn-primary mb-15" + (!!props.saleValue ? ' ml-10' : '')}
              title={t('mo.add_comission')}
              onClick={() => setShowSalesModal(true)}
            />
          </div>

          <div className="book-summary-total">
            <span className="title text-lg">{t('total')}: </span>
            <span>
              <span className="text-lg text-contrast mr-5">
                {formatPriceCurrency(getSalesPrice(), getCurrency(currency), true)}
              </span>
            </span>
          </div>

          {showSalesModal && (
            <div
              className="modal-overlay modal-overlay-top modal-maximizable"
              ref={modalOverlayRef}
              onClick={(e) => e.target === e.currentTarget && setShowSalesModal(false)}
            >
              <div ref={modalRef} className={'modal modal-sm active'} style={modalStyle}>
                <div
                  className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
                  {...dragHandleProps}
                >
                  <h5 className="title">{t('hotel.sales_price')}</h5>{' '}
                  <div data-no-drag="true">
                    <ScreenshotSupport />
                    <MaximizeModal modalOverlayRef={modalOverlayRef} />
                    <FontAwesomeIcon icon={faTimes} onClick={() => setShowSalesModal(false)} />
                  </div>
                </div>
                <div className="modal-content display-flex" style={{ marginTop: 30 }}>
                  <div className="mr-10" style={{ width: 40 }}>
                    <SelectCustom
                      options={[{ value: currency, label: getCurrency(currency) }]}
                      value={currency}
                      disabled
                      onChange={() => { }}
                    />
                  </div>
                  <div style={{ marginTop: -5, flex: '1 0 calc(100% - 200px)' }}>
                    <FormGroup
                      value={displaySaleValue}
                      onChange={setDisplaySaleValue}
                      label=""
                      allowOnlyNumbers
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <Button
                    title={t('add')}
                    className="btn btn-primary mr-a ml-0"
                    onClick={() => submit()}
                    disabled={loading}
                  />
                  <Button
                    title={t('close')}
                    className="btn btn-default"
                    onClick={() => setShowSalesModal(false)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default SalesPrice;
